import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, useToast } from '@/components/ui';
import {
  EVENT_LABELS,
  useCalendarActions,
  type CalendarEvent,
} from '@/features/calendar/api';
import { isApiError } from '@/lib/api';
import { affectedText, eventDates, recreateInput } from './event-format';
import styles from './calendar.module.css';

/**
 * A saved calendar entry (UAT RL-1047): what it is, and for staff who may
 * manage the calendar, "Change" (remove + add again, pre-filled) and "Remove"
 * (asks first, then offers Undo, which adds the same entry again).
 */
export function CalendarEventDialog({
  event,
  canManage,
  onClose,
  onEdit,
}: {
  event: CalendarEvent | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (event: CalendarEvent) => void;
}) {
  const { t } = useTranslation();
  const a = useCalendarActions();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);

  const close = () => {
    setConfirming(false);
    setFailure(null);
    onClose();
  };

  async function undo(e: CalendarEvent) {
    try {
      const back = await a.addEvent.mutateAsync(recreateInput(e));
      toast.show({
        tone: 'success',
        title: `${EVENT_LABELS[back.type]} added back`,
        body: affectedText(back.affectedCount, back.affected),
      });
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Could not add it back',
        body: isApiError(err) ? err.message : undefined,
      });
    }
  }

  async function remove(e: CalendarEvent) {
    setFailure(null);
    try {
      await a.removeEvent.mutateAsync(e.id);
    } catch (err) {
      setFailure(
        isApiError(err)
          ? { message: err.message, requestId: err.requestId }
          : { message: 'Could not remove the entry. Try again.' },
      );
      return;
    }
    close();
    toast.show(
      {
        tone: 'success',
        title: `${EVENT_LABELS[e.type]} removed`,
        body: e.description,
        action: { label: 'Undo', onClick: () => void undo(e) },
      },
      10_000,
    );
  }

  const e = event;
  return (
    <Dialog
      open={!!e}
      onClose={close}
      variant="modal"
      title={
        confirming
          ? 'Remove this calendar entry?'
          : e
            ? EVENT_LABELS[e.type]
            : ''
      }
      footer={
        e && canManage ? (
          confirming ? (
            <>
              <Button onClick={() => setConfirming(false)}>Keep it</Button>
              <Button
                variant="danger"
                icon={<Trash2 size={16} />}
                loading={a.removeEvent.isPending}
                onClick={() => void remove(e)}
              >
                Remove
              </Button>
            </>
          ) : (
            <>
              <Button
                icon={<Trash2 size={16} />}
                onClick={() => setConfirming(true)}
              >
                Remove
              </Button>
              <Button
                variant="primary"
                icon={<Pencil size={16} />}
                onClick={() => {
                  close();
                  onEdit(e);
                }}
              >
                Change
              </Button>
            </>
          )
        ) : undefined
      }
    >
      {e && (
        <div className={styles.stack}>
          {failure && (
            <p className={styles.note} role="alert">
              {failure.message}
              {failure.requestId && (
                <span className={styles.requestId}>
                  {t('common.requestId', { id: failure.requestId })}
                </span>
              )}
            </p>
          )}
          {confirming && (
            <p className={styles.warn}>
              &ldquo;{e.description}&rdquo; ({eventDates(e)}) is taken off the
              calendar; it stays on record. Bookings already marked to rebook
              stay marked and patients get no new message.
            </p>
          )}
          <dl className={styles.rows}>
            <dt>Type</dt>
            <dd>
              <Badge>{EVENT_LABELS[e.type]}</Badge>
            </dd>
            <dt>Dates</dt>
            <dd>{eventDates(e)}</dd>
            {e.shortHours && (
              <>
                <dt>Open</dt>
                <dd>{e.shortHours}</dd>
              </>
            )}
            <dt>Branch</dt>
            <dd>{e.branch?.name ?? 'Both branches'}</dd>
            {e.doctor && (
              <>
                <dt>Doctor</dt>
                <dd>{e.doctor.fullName}</dd>
              </>
            )}
            <dt>Description</dt>
            <dd>{e.description}</dd>
            <dt>Bookings</dt>
            <dd>{affectedText(e.affectedCount)}</dd>
          </dl>
        </div>
      )}
    </Dialog>
  );
}
